import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../hooks/useAuth";
import {
  selectCurrentUserData,
  selectSelectedTenantId,
  setSelectedTenantId,
} from "../features/auth/authSlice";
import {
  useGetTenantsQuery,
  useDeleteTenantMutation,
} from "../features/tenants/tenantsApiSlice";
import { apiSlice } from "../app/api/apiSlice";
import Iconify from "../components/iconify/Iconify";
import SubscriptionsTab from "../sections/@dashboard/tenants/SubscriptionsTab";

const formatLimit = (value) => {
  return Number.isFinite(value) ? value.toString() : "unbegrenzt";
};

const formatDateTime = (value) => {
  if (!value) return "–";
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_e) {
    return "–";
  }
};

const buildCountLabel = (current, limit) => {
  return `${current || 0}/${formatLimit(limit)}`;
};

function CountChip({ label, current, limit }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${label}: ${buildCountLabel(current, limit)}`}
    />
  );
}

function TenantMobileCard({
  tenant,
  isActive,
  isOverrideActive,
  onSwitch,
  onReset,
  onDelete,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        borderColor: (theme) =>
          isActive ? alpha(theme.palette.primary.main, 0.35) : "divider",
        bgcolor: (theme) =>
          isActive
            ? alpha(theme.palette.primary.main, 0.06)
            : "background.paper",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {tenant.tenantName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inhaber: {tenant.ownerName || "Nicht hinterlegt"}
              </Typography>
            </Box>
            <Chip
              size="small"
              color={isActive ? "primary" : "default"}
              label={
                isActive
                  ? isOverrideActive
                    ? "Override aktiv"
                    : "Aktiv"
                  : "Inaktiv"
              }
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Plan: ${tenant.plan || "starter"}`} />
            <CountChip
              label="Benutzer"
              current={tenant.currentUsers}
              limit={tenant.allowedUsers}
            />
            <CountChip
              label="Standorte"
              current={tenant.currentSites}
              limit={tenant.allowedSites}
            />
            <CountChip
              label="Mitglieder"
              current={tenant.currentMembers}
              limit={tenant.allowedMembers}
            />
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" color="text.secondary">
              Letzter Login: <strong>{formatDateTime(tenant.lastLogin)}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Letzte Aktivität:{" "}
              <strong>{formatDateTime(tenant.lastActivity)}</strong>
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              variant={isActive ? "outlined" : "contained"}
              onClick={() => onSwitch(tenant._id)}
              startIcon={<Iconify icon="mdi:swap-horizontal-bold" width={16} />}
            >
              {isActive ? "Aktiver Mandant" : "Mandant aktivieren"}
            </Button>
            {isActive && isOverrideActive ? (
              <Button
                color="inherit"
                variant="text"
                onClick={onReset}
                startIcon={<Iconify icon="mdi:restart" width={16} />}
              >
                Reset
              </Button>
            ) : null}
            <Button
              color="error"
              variant="text"
              onClick={() => onDelete(tenant)}
              startIcon={<Iconify icon="mdi:delete-outline" width={16} />}
            >
              Löschen
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function TenantPage() {
  const dispatch = useDispatch();
  const { isSuperAdmin } = useAuth();
  const currentUserData = useSelector(selectCurrentUserData);
  const selectedTenantId = useSelector(selectSelectedTenantId);
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const {
    data: tenants = [],
    isLoading,
    isError,
    refetch,
  } = useGetTenantsQuery();
  const [deleteTenant, { isLoading: isDeleting }] = useDeleteTenantMutation();

  // Action menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuTenantRef = useRef(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { _id, tenantName }
  const [deleteError, setDeleteError] = useState(null);

  const openMenu = (event, tenant) => {
    menuTenantRef.current = tenant;
    setMenuAnchor(event.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(null);

  const handleMenuSwitch = () => {
    closeMenu();
    applyTenantContext(menuTenantRef.current?._id);
  };

  const handleMenuDelete = () => {
    closeMenu();
    setDeleteTarget(menuTenantRef.current);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteTenant(deleteTarget._id).unwrap();
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      // If we were overriding to the deleted tenant, reset context.
      if (String(selectedTenantId) === String(deleteTarget._id)) {
        dispatch(setSelectedTenantId(null));
        dispatch(apiSlice.util.resetApiState());
      }
    } catch (err) {
      setDeleteError(err?.data?.message || "Löschen fehlgeschlagen.");
    }
  };

  const activeTenantId = selectedTenantId || currentUserData?.tenant || null;
  const overrideActive = Boolean(selectedTenantId);

  useEffect(() => {
    if (!selectedTenantId || !tenants.length) return;
    const tenantStillExists = tenants.some(
      (tenant) => String(tenant?._id) === String(selectedTenantId),
    );
    if (!tenantStillExists) {
      dispatch(setSelectedTenantId(null));
    }
  }, [dispatch, selectedTenantId, tenants]);

  const activeTenant = useMemo(() => {
    return tenants.find(
      (tenant) => String(tenant?._id) === String(activeTenantId),
    );
  }, [activeTenantId, tenants]);

  const filteredTenants = useMemo(() => {
    const normalizedSearch = String(searchValue || "")
      .trim()
      .toLowerCase();
    if (!normalizedSearch) return tenants;
    return tenants.filter((tenant) => {
      const values = [
        tenant?.tenantName || "",
        tenant?.ownerName || "",
        tenant?.plan || "",
      ];
      return values.some((value) =>
        value.toLowerCase().includes(normalizedSearch),
      );
    });
  }, [searchValue, tenants]);

  const totalUsers = useMemo(
    () => tenants.reduce((sum, tenant) => sum + (tenant?.currentUsers || 0), 0),
    [tenants],
  );
  const totalSites = useMemo(
    () => tenants.reduce((sum, tenant) => sum + (tenant?.currentSites || 0), 0),
    [tenants],
  );
  const totalMembers = useMemo(
    () =>
      tenants.reduce((sum, tenant) => sum + (tenant?.currentMembers || 0), 0),
    [tenants],
  );

  const applyTenantContext = (tenantId) => {
    const baseTenantId = currentUserData?.tenant
      ? String(currentUserData.tenant)
      : null;
    const nextTenantId = tenantId ? String(tenantId) : null;
    const nextOverrideId =
      nextTenantId && baseTenantId && nextTenantId !== baseTenantId
        ? nextTenantId
        : null;

    dispatch(setSelectedTenantId(nextOverrideId));
    dispatch(apiSlice.util.resetApiState());
  };

  const resetTenantContext = () => {
    dispatch(setSelectedTenantId(null));
    dispatch(apiSlice.util.resetApiState());
  };

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard/app" replace />;
  }

  return (
    <>
      <Helmet>
        <title> MA | Mandanten </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack spacing={2.5}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              color: "white",
              background:
                "linear-gradient(135deg, #0B3954 0%, #0F7173 52%, #2A9D8F 100%)",
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Mandantenverwaltung
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.92 }}>
                    SuperAdmin-Kontextwechsel mit Live-Übersicht über Plan- und
                    Nutzungslimits.
                  </Typography>
                </Box>
                <Chip
                  sx={{
                    color: "white",
                    borderColor: alpha("#fff", 0.45),
                    bgcolor: alpha("#000", 0.12),
                  }}
                  variant="outlined"
                  label={
                    overrideActive ? "Override aktiv" : "Basis-Kontext aktiv"
                  }
                />
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`Mandanten: ${tenants.length}`}
                  sx={{
                    color: "white",
                    borderColor: alpha("#fff", 0.45),
                    bgcolor: alpha("#000", 0.12),
                  }}
                  variant="outlined"
                />
                <Chip
                  label={`Benutzer: ${totalUsers}`}
                  sx={{
                    color: "white",
                    borderColor: alpha("#fff", 0.45),
                    bgcolor: alpha("#000", 0.12),
                  }}
                  variant="outlined"
                />
                <Chip
                  label={`Standorte: ${totalSites}`}
                  sx={{
                    color: "white",
                    borderColor: alpha("#fff", 0.45),
                    bgcolor: alpha("#000", 0.12),
                  }}
                  variant="outlined"
                />
                <Chip
                  label={`Mitglieder: ${totalMembers}`}
                  sx={{
                    color: "white",
                    borderColor: alpha("#fff", 0.45),
                    bgcolor: alpha("#000", 0.12),
                  }}
                  variant="outlined"
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{ borderRadius: 2.5, overflow: "hidden" }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Tab label="Mandanten" />
              <Tab label="Abos" />
            </Tabs>
          </Paper>

          {activeTab === 1 ? (
            <SubscriptionsTab />
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5 }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Aktiver Tenant-Kontext
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activeTenant?.tenantName || "Nicht gefunden"} (
                        {activeTenant?._id || activeTenantId || "-"})
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        disabled={!overrideActive}
                        onClick={resetTenantContext}
                        startIcon={<Iconify icon="mdi:restart" width={18} />}
                      >
                        Auf Basis-Kontext zuruecksetzen
                      </Button>
                    </Stack>
                  </Stack>

                  <TextField
                    fullWidth
                    size="small"
                    label="Mandant suchen"
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    placeholder="Name, Inhaber oder Plan"
                  />
                </Stack>
              </Paper>

              {isError ? (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={refetch}>
                      Erneut laden
                    </Button>
                  }
                >
                  Mandanten konnten nicht geladen werden.
                </Alert>
              ) : null}

              {isLoading ? (
                <Alert severity="info">Mandanten werden geladen...</Alert>
              ) : null}

              {!isLoading && !isError && filteredTenants.length === 0 ? (
                <Alert severity="warning">
                  Keine Mandanten passend zur Suche gefunden.
                </Alert>
              ) : null}

              {!isLoading && !isError ? (
                <>
                  <Stack
                    spacing={1.5}
                    sx={{ display: { xs: "flex", md: "none" } }}
                  >
                    {filteredTenants.map((tenant) => {
                      const isActive =
                        String(tenant?._id) === String(activeTenantId);
                      return (
                        <TenantMobileCard
                          key={tenant._id}
                          tenant={tenant}
                          isActive={isActive}
                          isOverrideActive={isActive && overrideActive}
                          onSwitch={applyTenantContext}
                          onReset={resetTenantContext}
                          onDelete={(t) => {
                            setDeleteTarget(t);
                            setDeleteError(null);
                            setDeleteDialogOpen(true);
                          }}
                        />
                      );
                    })}
                  </Stack>

                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2.5,
                      overflow: "hidden",
                      display: { xs: "none", md: "block" },
                    }}
                  >
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Inhaber</TableCell>
                            <TableCell>Admin-Mail</TableCell>
                            <TableCell>Plan</TableCell>
                            <TableCell>Benutzer</TableCell>
                            <TableCell>Standorte</TableCell>
                            <TableCell>Mitglieder</TableCell>
                            <TableCell>Letzter Login</TableCell>
                            <TableCell>Letzte Aktivität</TableCell>
                            <TableCell align="right">Aktion</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredTenants.map((tenant) => {
                            const isActive =
                              String(tenant?._id) === String(activeTenantId);
                            return (
                              <TableRow
                                key={tenant._id}
                                hover
                                sx={{
                                  bgcolor: isActive
                                    ? (theme) =>
                                        alpha(theme.palette.primary.main, 0.07)
                                    : "inherit",
                                }}
                              >
                                <TableCell>
                                  <Stack spacing={0.25}>
                                    <Typography variant="subtitle2">
                                      {tenant.tenantName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {tenant._id}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {tenant.ownerName || "Nicht hinterlegt"}
                                </TableCell>
                                <TableCell>
                                  {tenant.ownerMail || "Nicht hinterlegt"}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={tenant.plan || "starter"}
                                  />
                                </TableCell>
                                <TableCell>
                                  {buildCountLabel(
                                    tenant.currentUsers,
                                    tenant.allowedUsers,
                                  )}
                                </TableCell>
                                <TableCell>
                                  {buildCountLabel(
                                    tenant.currentSites,
                                    tenant.allowedSites,
                                  )}
                                </TableCell>
                                <TableCell>
                                  {buildCountLabel(
                                    tenant.currentMembers,
                                    tenant.allowedMembers,
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap>
                                    {formatDateTime(tenant.lastLogin)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap>
                                    {formatDateTime(tenant.lastActivity)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                    alignItems="center"
                                  >
                                    {isActive ? (
                                      <Chip
                                        size="small"
                                        color="primary"
                                        label={
                                          overrideActive
                                            ? "Override aktiv"
                                            : "Aktiv"
                                        }
                                      />
                                    ) : null}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => openMenu(e, tenant)}
                                      aria-label="Aktionen"
                                    >
                                      <Iconify
                                        icon="mdi:dots-vertical"
                                        width={18}
                                      />
                                    </IconButton>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </>
              ) : null}
            </>
          )}
        </Stack>
      </Container>

      {/* Three-dot action menu (desktop table) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleMenuSwitch}>
          <Iconify
            icon="mdi:swap-horizontal-bold"
            width={18}
            sx={{ mr: 1.5 }}
          />
          Wechseln
        </MenuItem>
        <MenuItem onClick={handleMenuDelete} sx={{ color: "error.main" }}>
          <Iconify icon="mdi:delete-outline" width={18} sx={{ mr: 1.5 }} />
          Löschen
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mandanten löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll der Mandant <strong>{deleteTarget?.tenantName}</strong>{" "}
            unwiderruflich gelöscht werden? Alle zugehörigen Benutzer, Standorte
            und Mitglieder werden ebenfalls gelöscht.
          </DialogContentText>
          {deleteError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Abbrechen
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            startIcon={<Iconify icon="mdi:delete-outline" width={16} />}
          >
            {isDeleting ? "Wird gelöscht…" : "Endgültig löschen"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
